import React, { useState } from "react";
import { format } from "date-fns";
import { ChatMessage } from "@/types/chat";
import { useAuthStore } from "@/store/authStore";
import { useChatStore } from "@/store/chat";
import { cn } from "@/lib/currencyFormatter";
import { MessageReactions } from "./MessageReactions";
import { MessageActionOverlay } from "./MessageActionOverlay";
import { EmojiReactionPicker } from "./EmojiReactionPicker";
import { useLongPress } from "@/hooks/useLongPress";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";

interface AttachmentBubbleProps {
  message: ChatMessage;
  onReply: (message: ChatMessage) => void;
}

export const AttachmentBubble: React.FC<AttachmentBubbleProps> = ({
  message,
  onReply,
}) => {
  const { user } = useAuthStore();
  const { deleteMessage, addReaction } = useChatStore();
  const isCurrentUser = user?.id === message.sender_id;

  // States for UI controls
  const [showActions, setShowActions] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Handle actions
  const handleDelete = () => {
    setShowActions(false);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    await deleteMessage(message.id);
    setShowDeleteConfirm(false);
  };

  const handleReactionClick = () => {
    setShowActions(false);
    setShowEmojiPicker(true);
  };

  const handleAddReaction = async (emoji: string) => {
    await addReaction(message.id, emoji);
    setShowEmojiPicker(false);
  };

  const handleReply = () => {
    setShowActions(false);
    onReply(message);
  };

  // Set up long press handler
  const { longPressProps } = useLongPress({
    onLongPress: () => setShowActions(true),
    onClick: () => {}, // Keep empty for now
  });

  // Format time to display outside the bubble
  const messageTime = format(new Date(message.created_at), "HH:mm");

  if (!message.attachment_url) {
    return null;
  }

  return (
    <div
      id={`message-${message.id}`}
      className={cn(
        "flex flex-col mb-3",
        isCurrentUser ? "items-end" : "items-start"
      )}
    >
      <div
        className={cn("chat-bubble", isCurrentUser ? "sender" : "receiver")}
        {...longPressProps}
      >
        {!isCurrentUser && (
          <div className="font-semibold text-xs mb-1">
            {message.sender_name}
          </div>
        )}

        {message.message_type === "image" ? (
          <img
            src={message.attachment_url}
            alt="Attachment"
            className="max-w-full rounded-md object-cover max-h-60"
          />
        ) : (
          <video
            src={message.attachment_url}
            controls
            className="max-w-full rounded-md max-h-60"
          />
        )}

        {message.content && (
          <div className="text-sm mt-2 whitespace-pre-wrap break-words">
            {message.content}
          </div>
        )}

        <MessageReactions message={message} />
      </div>

      {/* Time outside the bubble */}
      <span
        className={cn(
          "message-time",
          isCurrentUser ? "sender-time" : "receiver-time"
        )}
      >
        {messageTime}
      </span>

      {/* Full page action overlay */}
      {showActions && (
        <MessageActionOverlay
          onClose={() => setShowActions(false)}
          onDelete={handleDelete}
          onReact={handleReactionClick}
          onReply={handleReply}
          message={{
            content: message.content || "Media attachment",
            sender_name: message.sender_name,
          }}
        />
      )}

      {/* Emoji reaction picker */}
      <EmojiReactionPicker
        isOpen={showEmojiPicker}
        onClose={() => setShowEmojiPicker(false)}
        onSelectEmoji={handleAddReaction}
      />

      {/* Delete confirmation dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Message</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this message? This action cannot
              be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
